import GiftWidget from '../../widgets/GiftWidget';
import { tryFetch } from '../../widgetLoader';
import { loadWidgetData, applyWidgetData, RELOAD_INTERVAL } from './common';

const DATA = [ 'eventTypes', 'giftTypes', 'giftThresholds', 'textReplacements', 'serviceIcons', 'userData' ];

export default async function mountGift(host) {
	const data = await loadWidgetData(host, DATA);
	const container = host.container;

	// Forward alert status events to the API
	container.addEventListener('alertStatus', ({ detail }) => {
		host.api.post(
			`widget/${encodeURIComponent(host.hash)}/alert/${encodeURIComponent(detail.dbId)}/status`,
			{ status: detail.status, ...(detail.err ? { err: detail.err } : {}) }
		).catch(() => {});
	});

	const widget = new GiftWidget({
		container,
		soundManager: host.soundManager,
		voiceApi: host.voiceApi,
		mmlApi: host.mmlApi,
		baseUrl: host.baseUrl,
	});
	await widget.init({ ...data });

	// Fetch all unplayed alerts from the DB and enqueue them in the widget.
	// Used both for the loadQueue startup option and when resuming from stopped state.
	const loadUnplayedAlerts = async () => {
		const alerts = await host.api.get(`widget/${encodeURIComponent(host.hash)}/alerts`, { readyOnly: true }).catch(() => []);
		for(const alert of alerts || []) widget._onAlert(alert);
	};

	if(data.settings?.stopped) {
		widget.queue.pause();
	} else if(data.settings?.loadQueue) {
		await loadUnplayedAlerts();
	}

	host.onSettingsChange(s => widget.applySettings(s));
	host.onFieldChange(async (field, value) => {
		const wasStopped = !!widget.settings.stopped;
		widget.applySettings({ ...widget.settings, [field]: value });
		if(field === 'stopped') {
			if(value) {
				widget.stopAlerts();
			} else if(wasStopped) {
				await loadUnplayedAlerts();
				widget.queue.resume();
			}
		}
	});

	const reload = async () => applyWidgetData(widget, await loadWidgetData(host, DATA));

	host.subscribe({
		reconnect: reload,
		// Don't enqueue incoming alerts while stopped — they stay in the DB and are
		// bulk-loaded via loadUnplayedAlerts() when the widget is resumed.
		userWidgetAlert: msg => {
			if(!widget.settings.stopped) widget._onAlert(msg);
		},
		userWidgetAlertDelete: () => {},
		alertQueuePause: () => widget.queue.pause(),
		alertQueueResume: () => widget.queue.resume(),
		alertQueueSkip: () => widget.queue.skip(),
		userTextReplacementsChange: msg => widget.applyTextReplacements(msg),
		userEventTypeChange: msg => widget.applyEventType(msg.eventType, msg.data),
		userGiftTypeChange: msg => widget.applyGiftType(msg.serviceId, msg.giftType, msg.data),
		userGiftThresholdsChange: async () => {
			const d = await tryFetch(() => host.api.get(`widget/${encodeURIComponent(host.hash)}/giftThresholds`), `cache-${host.hash}-giftThresholds`);
			if(d) widget.applyGiftThresholds(d);
		},
	});

	host.every(reload, RELOAD_INTERVAL);
	host.onDestroy(() => widget.destroy());
}
